const WEATHER_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const PRESSURE_SWEET_SPOT_MIN_INHG = 29.9;
const PRESSURE_SWEET_SPOT_MAX_INHG = 30.3;
const PRESSURE_TREND_THRESHOLD_HPA = 1; // ±1 hPa = rising or falling

// ---------------------------------------------------------------------------
// Types — raw API response shape (OWM One Call 3.0 with units=imperial)
// ---------------------------------------------------------------------------

interface OWMWeatherCondition {
  main: string;
  description: string;
  icon: string;
}

interface OWMCurrentResponse {
  dt: number;
  temp: number;
  feels_like: number;
  humidity: number;
  pressure: number; // hPa
  wind_speed: number; // mph (imperial)
  wind_deg: number;
  wind_gust?: number; // mph
  clouds: number;
  visibility: number;
  weather: OWMWeatherCondition[];
}

interface OWMHourlyResponse {
  dt: number;
  temp: number;
  feels_like: number;
  humidity: number;
  pressure: number; // hPa
  wind_speed: number;
  wind_deg: number;
  weather: OWMWeatherCondition[];
  pop: number;
  rain?: { '1h'?: number }; // mm — present when precipitation occurred
  snow?: { '1h'?: number }; // mm — present when snowfall occurred
}

interface OWMDailyTemp {
  day: number;
  min: number;
  max: number;
}

interface OWMDailyResponse {
  dt: number;
  temp: OWMDailyTemp;
  pressure: number; // hPa
  humidity: number;
  wind_speed: number;
  wind_deg: number;
  weather: OWMWeatherCondition[];
  pop: number;
  summary?: string;
  rain?: number; // mm total for the day
  snow?: number; // mm total for the day
}

interface OWMOneCallResponse {
  lat: number;
  lon: number;
  timezone: string;
  current: OWMCurrentResponse;
  hourly: OWMHourlyResponse[];
  daily: OWMDailyResponse[];
}

// ---------------------------------------------------------------------------
// Public types — normalized internal representation
// ---------------------------------------------------------------------------

export interface WeatherData {
  current: {
    temp: number; // °F
    feelsLike: number; // °F
    humidity: number; // %
    pressure: number; // hPa from API — convert to inHg for display
    windSpeed: number; // mph
    windDeg: number; // degrees meteorological
    windGust?: number; // mph
    clouds: number; // % cloud cover
    visibility: number; // meters
    condition: string; // e.g., "Clouds"
    conditionDetail: string; // e.g., "overcast clouds"
    icon: string; // OWM icon code e.g., "04d"
    dt: number; // Unix timestamp
  };

  hourly: Array<{
    dt: number;
    temp: number;
    feelsLike: number;
    pressure: number; // hPa
    humidity: number;
    windSpeed: number;
    windDeg: number;
    condition: string;
    conditionDetail: string;
    icon: string;
    pop: number; // probability of precipitation 0–1
    rainMm?: number; // mm in the past hour — present when precipitation occurred
    snowMm?: number; // mm in the past hour — present when snowfall occurred
  }>;

  daily: Array<{
    dt: number;
    tempDay: number;
    tempMin: number;
    tempMax: number;
    pressure: number; // hPa
    humidity: number;
    windSpeed: number;
    windDeg: number;
    condition: string;
    conditionDetail: string;
    icon: string;
    pop: number;
    summary?: string;
    rain?: number; // mm total for the day
    snow?: number; // mm total for the day
  }>;

  fetchedAt: number; // Unix timestamp
  lat: number;
  lon: number;
  timezone: string;
}

interface CachedWeather {
  data: WeatherData;
  propertyId: string;
  fetchedAt: number;
}

export type PressureTrend = 'rising' | 'falling' | 'steady';

// Derived display values computed from raw WeatherData
export interface WeatherConditionsDisplay {
  temp: number; // °F
  feelsLike: number; // °F
  tempToday: number | null; // Today's daily high for comparison
  tempDelta: number | null; // current - today's high (negative = cooler)
  tempDeltaFavorable: boolean; // true if drop ≥ 10°F (ADR-017)
  pressure: number; // inHg
  pressureTrend: PressureTrend;
  pressureInSweetSpot: boolean; // 29.90–30.30 inHg
  windSpeed: number; // mph
  windDeg: number; // meteorological degrees (0=N, 90=E, 180=S, 270=W)
  windDirection: string; // cardinal e.g., "NW"
  windGust?: number; // mph
  humidity: number; // %
  condition: string; // "Overcast Clouds"
  icon: string; // OWM icon code
  lastUpdated: Date;
  precipitationProbability?: number; // 0-100, percentage
  precipitationVolume?: number;      // inches
}

// ---------------------------------------------------------------------------
// In-memory cache
// ---------------------------------------------------------------------------

const weatherCache = new Map<string, CachedWeather>();

function isCacheValid(cached: CachedWeather): boolean {
  return Date.now() - cached.fetchedAt < WEATHER_CACHE_TTL_MS;
}

// ---------------------------------------------------------------------------
// Utility functions — pure, no side effects
// ---------------------------------------------------------------------------

export function hpaToInHg(hpa: number): number {
  return Math.round(hpa * 0.02953 * 100) / 100; // 2 decimal places
}

const CARDINAL_DIRECTIONS = [
  'N', 'NNE', 'NE', 'ENE',
  'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW',
  'W', 'WNW', 'NW', 'NNW',
] as const;

export function windDegToCardinal(deg: number): string {
  const index = Math.round(deg / 22.5) % 16;
  return CARDINAL_DIRECTIONS[index];
}

/**
 * Compute pressure trend by comparing hourly[0] (current) to hourly[3] (3 hrs later).
 * A rising future means pressure is currently building upward; falling means dropping.
 * If < 4 entries available, compares to the last entry.
 */
export function computePressureTrend(
  hourlyPressures: Array<{ dt: number; pressure: number }>,
): PressureTrend {
  if (hourlyPressures.length < 2) return 'steady';

  const current = hourlyPressures[0].pressure;
  const compareIndex = Math.min(3, hourlyPressures.length - 1);
  const future = hourlyPressures[compareIndex].pressure;
  const delta = future - current;

  if (delta >= PRESSURE_TREND_THRESHOLD_HPA) return 'rising';
  if (delta <= -PRESSURE_TREND_THRESHOLD_HPA) return 'falling';
  return 'steady';
}

export function isPressureInSweetSpot(pressureInHg: number): boolean {
  return pressureInHg >= PRESSURE_SWEET_SPOT_MIN_INHG && pressureInHg <= PRESSURE_SWEET_SPOT_MAX_INHG;
}

/**
 * Select the hourly entry whose dt is closest to the given nowMs timestamp.
 * Returns null if the hourly array is empty.
 */
export function selectCurrentHourly(
  hourly: WeatherData['hourly'],
  nowMs: number = Date.now(),
): WeatherData['hourly'][number] | null {
  if (hourly.length === 0) return null;

  let bestIdx = 0;
  let bestDiff = Math.abs(hourly[0].dt * 1000 - nowMs);

  for (let i = 1; i < hourly.length; i++) {
    const diff = Math.abs(hourly[i].dt * 1000 - nowMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }

  return hourly[bestIdx];
}

export function computeConditionsDisplay(data: WeatherData, nowMs: number = Date.now()): WeatherConditionsDisplay {
  const { current, hourly, daily } = data;

  // Select the hourly entry closest to now so conditions reflect the current hour,
  // not the fetch-time snapshot (which may be stale if cache is aging).
  const nearest = selectCurrentHourly(hourly, nowMs);
  const src = nearest ?? current;

  const pressureInHg = hpaToInHg(src.pressure);
  const pressureTrend = computePressureTrend(hourly);
  const windDirection = windDegToCardinal(src.windDeg);

  // Compare current temp to today's daily high (daily[0].tempMax)
  const tempToday = daily.length > 0 ? daily[0].tempMax : null;
  const tempDelta = tempToday !== null ? src.temp - tempToday : null;
  // Favorable if current temp is ≥10°F below today's expected high (notable cold front)
  const tempDeltaFavorable = tempDelta !== null && tempDelta <= -10;

  // Precipitation probability — from the selected hourly entry's pop (0–1 → 0–100).
  // `current` does not carry `pop`, so probability is only available when we have a
  // valid hourly entry (nearest !== null).
  const precipitationProbability: number | undefined =
    nearest !== null ? Math.round(nearest.pop * 100) : undefined;

  // Precipitation volume — rain + snow from hourly (mm → inches, rounded to 2 decimal places).
  let precipitationVolume: number | undefined;
  if (nearest !== null) {
    const rainMm = nearest.rainMm ?? 0;
    const snowMm = nearest.snowMm ?? 0;
    const totalMm = rainMm + snowMm;
    precipitationVolume = Math.round((totalMm / 25.4) * 100) / 100;
  }

  return {
    temp: src.temp,
    feelsLike: src.feelsLike,
    tempToday,
    tempDelta,
    tempDeltaFavorable,
    pressure: pressureInHg,
    pressureTrend,
    pressureInSweetSpot: isPressureInSweetSpot(pressureInHg),
    windSpeed: src.windSpeed,
    windDeg: src.windDeg,
    windDirection,
    windGust: current.windGust, // wind_gust only available on current, not hourly
    humidity: src.humidity,
    condition: src.conditionDetail
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' '),
    icon: src.icon,
    lastUpdated: new Date(data.fetchedAt),
    precipitationProbability,
    precipitationVolume,
  };
}

// ---------------------------------------------------------------------------
// API response mapping
// ---------------------------------------------------------------------------

function mapOWMResponse(raw: OWMOneCallResponse, fetchedAt: number): WeatherData {
  const currentWeather = raw.current.weather[0];
  return {
    current: {
      temp: raw.current.temp,
      feelsLike: raw.current.feels_like,
      humidity: raw.current.humidity,
      pressure: raw.current.pressure,
      windSpeed: raw.current.wind_speed,
      windDeg: raw.current.wind_deg,
      windGust: raw.current.wind_gust,
      clouds: raw.current.clouds,
      visibility: raw.current.visibility,
      condition: currentWeather?.main ?? '',
      conditionDetail: currentWeather?.description ?? '',
      icon: currentWeather?.icon ?? '',
      dt: raw.current.dt,
    },
    hourly: raw.hourly.map((h) => {
      const hw = h.weather[0];
      return {
        dt: h.dt,
        temp: h.temp,
        feelsLike: h.feels_like,
        pressure: h.pressure,
        humidity: h.humidity,
        windSpeed: h.wind_speed,
        windDeg: h.wind_deg,
        condition: hw?.main ?? '',
        conditionDetail: hw?.description ?? '',
        icon: hw?.icon ?? '',
        pop: h.pop,
        rainMm: h.rain?.['1h'],
        snowMm: h.snow?.['1h'],
      };
    }),
    daily: raw.daily.map((d) => {
      const dw = d.weather[0];
      return {
        dt: d.dt,
        tempDay: d.temp.day,
        tempMin: d.temp.min,
        tempMax: d.temp.max,
        pressure: d.pressure,
        humidity: d.humidity,
        windSpeed: d.wind_speed,
        windDeg: d.wind_deg,
        condition: dw?.main ?? '',
        conditionDetail: dw?.description ?? '',
        icon: dw?.icon ?? '',
        pop: d.pop,
        summary: d.summary,
        rain: d.rain,
        snow: d.snow,
      };
    }),
    fetchedAt,
    lat: raw.lat,
    lon: raw.lon,
    timezone: raw.timezone,
  };
}

// ---------------------------------------------------------------------------
// Core API call
// ---------------------------------------------------------------------------

export async function fetchWeatherData(lat: number, lng: number): Promise<WeatherData | null> {
  const apiKey = import.meta.env.VITE_OWM_API_KEY as string | undefined;
  if (!apiKey) {
    console.warn('[weather-service] VITE_OWM_API_KEY is not set — weather unavailable');
    return null;
  }

  const url = `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lng}&units=imperial&appid=${apiKey}`;

  let response: Response;
  try {
    response = await fetch(url);
  } catch (err) {
    console.error('[weather-service] Network error fetching weather:', err);
    return null;
  }

  if (response.status === 401) {
    console.error('[weather-service] Invalid API key (401) — check VITE_OWM_API_KEY');
    return null;
  }

  if (response.status === 429) {
    console.warn('[weather-service] Rate limit reached (429) — using cached data if available');
    return null;
  }

  if (!response.ok) {
    console.error(`[weather-service] Unexpected response status: ${response.status}`);
    return null;
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch (err) {
    console.error('[weather-service] Failed to parse weather response JSON:', err);
    return null;
  }

  const fetchedAt = Date.now();
  return mapOWMResponse(raw as OWMOneCallResponse, fetchedAt);
}

// ---------------------------------------------------------------------------
// Cached fetch
// ---------------------------------------------------------------------------

export async function getWeatherForProperty(
  propertyId: string,
  lat: number,
  lng: number,
): Promise<WeatherData | null> {
  const cached = weatherCache.get(propertyId);
  if (cached && isCacheValid(cached)) {
    return cached.data;
  }

  const fresh = await fetchWeatherData(lat, lng);

  if (fresh) {
    weatherCache.set(propertyId, { data: fresh, propertyId, fetchedAt: fresh.fetchedAt });
    return fresh;
  }

  // On failure, return stale cache as degraded mode
  if (cached) {
    console.warn(`[weather-service] Using stale cache for property ${propertyId}`);
    return cached.data;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Force refresh — bypasses cache
// ---------------------------------------------------------------------------

export async function refreshWeatherForProperty(
  propertyId: string,
  lat: number,
  lng: number,
): Promise<WeatherData | null> {
  const fresh = await fetchWeatherData(lat, lng);

  if (fresh) {
    weatherCache.set(propertyId, { data: fresh, propertyId, fetchedAt: fresh.fetchedAt });
    return fresh;
  }

  // On failure, return whatever is in cache (may be stale)
  return weatherCache.get(propertyId)?.data ?? null;
}

// Exported for testing only — clears the in-memory cache
export function _clearWeatherCache(): void {
  weatherCache.clear();
}
